export interface UploadPartHandle {
  xhr: XMLHttpRequest | null
}

export interface UploadPartOptions {
  url: string
  blob: Blob
  onProgress: (loaded: number) => void
  handle: UploadPartHandle
}

/**
 * PUT a single multipart chunk via XMLHttpRequest so we get upload progress
 * (fetch() in browsers does not report upload progress). Resolves with the
 * ETag returned by S3 (quotes stripped). Rejects on non-2xx or network errors.
 */
export function uploadPartWithProgress(opts: UploadPartOptions): Promise<string> {
  const { url, blob, onProgress, handle } = opts

  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    handle.xhr = xhr
    xhr.open('PUT', url, true)

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (e.lengthComputable) onProgress(e.loaded)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const raw = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag') || ''
        const etag = raw.replace(/"/g, '')

        if (!etag) {
          reject(new Error('Part upload succeeded but ETag header is missing (check S3 CORS ExposeHeaders)'))

          return
        }

        resolve(etag)
      } else {
        reject(new Error(`Part upload failed: ${xhr.status} ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Network error during part upload')))
    xhr.addEventListener('abort', () => reject(new Error('Part upload aborted')))

    xhr.send(blob)
  })
}

export function isRetryableUploadError(err: unknown): boolean {
  if (!(err instanceof Error)) return true
  const msg = err.message
  // Don't retry 4xx (expired presigned URL, auth issues) — fail fast.
  const m = /Part upload failed: (\d+)/.exec(msg)

  if (m) {
    const code = parseInt(m[1] ?? '0', 10)

    return code >= 500
  }

  // Retry network errors & missing-etag
  return true
}

export async function uploadPartWithRetry(
  opts: UploadPartOptions,
  maxAttempts = 3,
  checkAborted: () => boolean = () => false,
): Promise<string> {
  let attempt = 0
  let lastErr: unknown

  while (attempt < maxAttempts) {
    if (checkAborted()) throw new Error('Aborted by user')
    try {
      return await uploadPartWithProgress(opts)
    } catch (err) {
      lastErr = err
      if (checkAborted()) throw err
      if (!isRetryableUploadError(err)) throw err
      attempt++
      if (attempt >= maxAttempts) break
      const delay = 1000 * Math.pow(2, attempt - 1)
      await new Promise(r => setTimeout(r, delay))
    }
  }

  throw lastErr instanceof Error ? lastErr : new Error('Part upload failed after retries')
}
